/**
 * Centralised Data Layer helper for GTM event tracking.
 *
 * All calculator events are pushed via `window.dataLayer` so that
 * Google Tag Manager can pick them up. The helper is defensive:
 * - no-ops during SSR / tests where `window` is undefined
 * - initialises `dataLayer` if missing
 * - also tries to push to `window.parent.dataLayer` when the app runs
 *   inside a same-origin iframe (mazda.ca host), falling back silently
 *   on cross-origin.
 */

export interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

/**
 * Push an event payload to the GTM dataLayer.
 * @param eventName - GTM event name e.g. 'calculator_slider_interaction'
 * @param params - optional extra fields merged into the payload
 */
export function pushDataLayerEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') return;

  const payload: DataLayerEvent = {
    event: eventName,
    ...params,
  };

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch {
    // ignore – dataLayer push should never break the app
  }

  // Attempt to mirror the event to the parent frame's dataLayer so that
  // a host-page GTM container can also capture it when same-origin.
  try {
    if (window.parent && window.parent !== window) {
      const parent = window.parent as Window;
      parent.dataLayer = parent.dataLayer || [];
      // Avoid double-push when parent and window share the same array reference
      if (parent.dataLayer !== window.dataLayer) {
        parent.dataLayer.push(payload);
      }
    }
  } catch {
    // Cross-origin parent – ignore
  }
}
